import { useState } from 'react';
import { withRouter } from 'react-router-dom';
import LoginImage from '@assets/images/logo/login.svg';
import { swalMessage } from '@components/common';
import AppStoreButtons from '@components/common/AppStoreButtons';
import { postMerchantLogin, postUserLogin } from '@lib/api/v2/auth';
import useHandleAPICall from '@lib/hooks/useHandleAPICall';
import usePasskey from '@lib/hooks/usePasskey';
import useAppStore from '@store/useAppStore';
import { swalReturnWarning } from '@util/commonFunctions';
import LOG_PREFIX from '@util/logger/constants';
import { createLogger } from '@util/logger/logger';
import styled, { createGlobalStyle, css } from 'styled-components';
import Swal from 'sweetalert2';

const logger = createLogger(LOG_PREFIX.AUTH);

const seatPreview = [
  { label: 'A1', state: 'available' },
  { label: 'A2', state: 'occupied' },
  { label: 'A3', state: 'available' },
  { label: 'A4', state: 'selected' },
  { label: 'B1', state: 'occupied' },
  { label: 'B2', state: 'available' },
  { label: 'B3', state: 'available' },
  { label: 'B4', state: 'occupied' },
];

const Logins = () => {
  const { setUserPhoneNumber } = useAppStore();
  const { createRequest } = useHandleAPICall();
  const { isPasskeySupported, authenticate } = usePasskey();
  const [LoginData, setLoginData] = useState({ id: '', password: '' });
  const [selectedTab, setSelectedTab] = useState('admin');
  const [togglePassword, setTogglePassword] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const { id, password } = LoginData;
  const loginIdLabel = selectedTab === 'admin' ? '지점 번호' : '휴대폰 번호';
  const loginIdPlaceholder =
    selectedTab === 'admin' ? '지점 번호를 입력해 주세요' : '휴대폰 번호를 입력해 주세요';
  // 관리자(점주/최고관리자) 탭에서만, 지원 브라우저/오리진일 때만 패스키 버튼 노출
  const showPasskeyButton = selectedTab === 'admin' && isPasskeySupported();
  const handleOnChange = (e) => {
    setLoginData({ ...LoginData, [e.target.name]: e.target.value });
  };

  const handlePasskeyLogin = async () => {
    if (passkeyBusy) return;
    setPasskeyBusy(true);
    try {
      const loginType = await authenticate(id);
      if (loginType === 'admin') {
        // 이미 패스키 보유 → 등록 권유 생략, 목적지 직행
        window.location.href = '/backoffice/merchant/list';
      } else if (loginType === 'merchant') {
        window.location.href = '/admin/dashboard';
      } else {
        // null = 취소 또는 실패. 등록된 패스키가 없을 수 있음.
        Swal.fire({
          text: id
            ? '패스키 로그인에 실패했습니다.\n비밀번호로 로그인하거나, 로그인 후 패스키를 등록해 주세요.'
            : '기존 패스키는 지점 번호를 입력하고 다시 시도해 주세요.\n아이디 없이 로그인하려면 비밀번호로 로그인 후 패스키를 다시 등록해 주세요.',
          confirmButtonColor: '#00bec1',
        });
      }
    } catch (err) {
      logger.error('패스키 로그인 에러', {
        error: err instanceof Error ? err.message : 'Unknown error',
      });
      swalMessage('error', '에러가 발생했습니다.\n잠시 후 다시 시도해 주세요.', '확인', false);
    } finally {
      setPasskeyBusy(false);
    }
  };

  const handleLogin = async () => {
    try {
      let res;
      if (selectedTab === 'admin') res = await createRequest(postMerchantLogin, id, password);
      if (selectedTab === 'user') res = await createRequest(postUserLogin, id, password);

      if (res.status === 200) {
        const { data } = res;
        logger.info('로그인 성공', { userType: selectedTab });
        if (selectedTab === 'user') setUserPhoneNumber(data?.phoneNum || '');
      } else if (res.status === 202 && res.data === 102) {
        logger.warn('로그인 실패 - 아이디 오류', { userType: selectedTab });
        Swal.fire({
          text: '아이디가 잘못됐습니다. 다시 입력해주세요.',
          confirmButtonColor: '#00bec1',
        });
      } else if (res.status === 202 && res.data === 101) {
        logger.warn('로그인 실패 - 비밀번호 오류', { userType: selectedTab });
        Swal.fire({
          text: '비밀번호가 잘못됐습니다. 다시 입력해주세요.',
          confirmButtonColor: '#00bec1',
        });
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      logger.error('로그인 에러', {
        userType: selectedTab,
        error: errorMessage,
      });
      if (errorMessage.includes('401') || errorMessage.includes('500'))
        return swalReturnWarning(
          `아이디 또는 비밀번호를 잘못 입력했습니다.\n\n키오스크에서 회원가입하신 경우 비밀번호를 재설정해 주세요.`,
        );
      swalMessage('error', '에러가 발생했습니다.\n잠시 후 다시 시도해 주세요.', '확인', false);
    }
  };

  const onClickjoin = () => {
    window.location.href = `${process.env.PUBLIC_URL}/auth/join`;
  };

  const onClickPassword = () => {
    window.location.href = `${process.env.PUBLIC_URL}/auth/password`;
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleLogin();
  };

  return (
    <Page>
      <ResponsiveStyles />
      <Shell>
        <BrandPanel>
          <BrandHeader>
            <Logo src={LoginImage} alt="렛미업" />
            <SupportNote>더 쉬운 스터디카페 이용과 운영</SupportNote>
          </BrandHeader>

          <HeroCopy>
            <Eyebrow>공간을 쓰는 모든 순간</Eyebrow>
            <HeroTitle>
              스터디카페 이용을,
              <br />
              <Accent>기다림 없이</Accent>
            </HeroTitle>
            <HeroDescription>
              <span>입장부터 좌석 확인, 이용권 구매와 예약까지.</span>
              <span>점주님과 이용자 모두에게 필요한 다음 행동만 보여드려요.</span>
            </HeroDescription>
          </HeroCopy>

          <ProductVisual aria-hidden="true">
            <ProductTopBar>
              <ProductLabel>
                <LiveDot />
                실시간 매장 현황
              </ProductLabel>
              <ProductLocation>제품 화면 예시</ProductLocation>
            </ProductTopBar>
            <ProductBody>
              <SeatSection>
                <SeatSectionHeader>
                  <div>
                    <VisualCaption>좌석 현황</VisualCaption>
                    <VisualTitle>지금 비어 있는 자리를 한눈에</VisualTitle>
                  </div>
                  <Availability>이용 가능</Availability>
                </SeatSectionHeader>
                <SeatGrid>
                  {seatPreview.map((seat) => (
                    <Seat key={seat.label} $state={seat.state}>
                      {seat.label}
                    </Seat>
                  ))}
                </SeatGrid>
                <SeatLegend>
                  <span><i data-state="available" />이용 가능</span>
                  <span><i data-state="occupied" />이용 중</span>
                  <span><i data-state="selected" />선택한 좌석</span>
                </SeatLegend>
              </SeatSection>

              <PhonePreview>
                <PhoneNotch />
                <PhoneBrand>letmeup</PhoneBrand>
                <PhoneGreeting>안녕하세요</PhoneGreeting>
                <PhoneTitle>오늘의 이용을<br />바로 시작해 보세요</PhoneTitle>
                <PhoneAction>
                  <span>내 이용권</span>
                  <strong>이용권 사용 중</strong>
                </PhoneAction>
                <PhoneButton>좌석 확인하기</PhoneButton>
              </PhonePreview>
            </ProductBody>
          </ProductVisual>

          <ExperienceFlow aria-label="렛미업 주요 이용 흐름">
            <span>입장</span><i />
            <span>좌석 확인</span><i />
            <span>이용권</span><i />
            <span>예약</span>
          </ExperienceFlow>

          {selectedTab === 'user' && (
            <DesktopAppPromo>
              <AppPromoCopy>
                <strong>렛미업 앱으로 더 편하게</strong>
                <span>매장에 도착하기 전부터 이용을 시작하세요.</span>
              </AppPromoCopy>
              <HeroStoreButtons />
            </DesktopAppPromo>
          )}
        </BrandPanel>

        <LoginPanel>
          <LoginContent>
            <LoginHeader>
              <LoginKicker>LETMEUP LOGIN</LoginKicker>
              <LoginTitle>로그인</LoginTitle>
              <LoginDescription>이용 목적에 맞는 계정을 선택해 주세요.</LoginDescription>
            </LoginHeader>

            <RoleTabs role="tablist" aria-label="로그인 유형">
              <RoleTab
                type="button"
                role="tab"
                aria-selected={selectedTab === 'admin'}
                $selected={selectedTab === 'admin'}
                onClick={() => setSelectedTab('admin')}>
                점주 · 관리자
              </RoleTab>
              <RoleTab
                type="button"
                role="tab"
                aria-selected={selectedTab === 'user'}
                $selected={selectedTab === 'user'}
                onClick={() => setSelectedTab('user')}>
                이용자
              </RoleTab>
            </RoleTabs>

            <Fields>
              <Field>
                <FieldLabel htmlFor="login-id">{loginIdLabel}</FieldLabel>
                <TextInput
                  id="login-id"
                  placeholder={loginIdPlaceholder}
                  type="text"
                  name="id"
                  required
                  value={id}
                  onChange={handleOnChange}
                  onKeyDown={handleKeyDown}
                  autoComplete="username"
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="login-password">비밀번호</FieldLabel>
                <TextInput
                  id="login-password"
                  placeholder="비밀번호를 입력해 주세요"
                  type={togglePassword ? 'text' : 'password'}
                  name="password"
                  value={password}
                  onChange={handleOnChange}
                  onKeyDown={handleKeyDown}
                  autoComplete="current-password"
                  required
                />
                <EyeButton
                  type="button"
                  aria-label={togglePassword ? '비밀번호 숨기기' : '비밀번호 보기'}
                  onClick={() => setTogglePassword(!togglePassword)}>
                  <EyeIcon open={togglePassword} />
                </EyeButton>
              </Field>
              <ForgotRow>
                <ForgotLink type="button" onClick={onClickPassword}>
                  비밀번호를 잊으셨나요?
                </ForgotLink>
              </ForgotRow>
            </Fields>

            <PrimaryButton type="button" onClick={handleLogin}>
              로그인
            </PrimaryButton>

            {showPasskeyButton && (
              <GhostButton type="button" onClick={handlePasskeyLogin} disabled={passkeyBusy}>
                <PasskeyIcon />
                {passkeyBusy ? '인증 중...' : '패스키로 로그인'}
              </GhostButton>
            )}

            <JoinRow>
              <JoinText>렛미업이 처음이신가요?</JoinText>
              <JoinButton type="button" onClick={onClickjoin}>회원가입</JoinButton>
            </JoinRow>

            {selectedTab === 'user' && (
              <MobileAppPromo>
                <AppPromoCopy>
                  <strong>앱으로 더 편하게 이용하세요</strong>
                  <span>좌석 확인부터 이용권 구매, 예약까지 한 번에.</span>
                </AppPromoCopy>
                <HeroStoreButtons />
              </MobileAppPromo>
            )}
          </LoginContent>
          <LoginFooter>안전한 로그인을 위해 계정 정보를 다른 사람과 공유하지 마세요.</LoginFooter>
        </LoginPanel>
      </Shell>
    </Page>
  );
};

export default withRouter(Logins);

/* ── 아이콘 ───────────────────────────────────────── */
const EyeIcon = ({ open }) =>
  open ? (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ) : (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c6.5 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
      <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3.5 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
      <line x1="2" y1="2" x2="22" y2="22" />
    </svg>
  );

const PasskeyIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="8" cy="15" r="4" />
    <path d="M12 15h9M18 15v3M15 15v2" />
    <path d="M5 10V8a7 7 0 0 1 13.4-2.8" />
  </svg>
);

/* ── 레이아웃 ─────────────────────────────────────── */
const Page = styled.div`
  min-height: 100vh;
  min-height: 100dvh;
  background: ${({ theme }) => theme.background};
  color: ${({ theme }) => theme.darkNavy};
  overflow-x: hidden;
`;

const Shell = styled.main`
  width: 100%;
  max-width: 1600px;
  min-height: 100vh;
  min-height: 100dvh;
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(420px, 480px);
  background: ${({ theme }) => theme.white};
`;

const BrandPanel = styled.section`
  position: relative;
  min-width: 0;
  padding: clamp(32px, 4.5vw, 72px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: color-mix(in oklch, ${({ theme }) => theme.primary} 5%, ${({ theme }) => theme.background});

  &::after {
    content: '';
    position: absolute;
    width: clamp(220px, 24vw, 380px);
    aspect-ratio: 1;
    right: clamp(-160px, -8vw, -64px);
    top: 24%;
    border: clamp(36px, 4vw, 64px) solid color-mix(in oklch, ${({ theme }) => theme.primary} 18%, transparent);
    border-radius: 50%;
    pointer-events: none;
  }
`;

const BrandHeader = styled.header`
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
`;

const Logo = styled.img`
  height: 36px;
  width: auto;
`;

const SupportNote = styled.span`
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  padding: 0 4px;
  color: ${({ theme }) => theme.darkNavyAlpha[800]};
  font-size: 14px;
  font-weight: 600;
`;

const HeroCopy = styled.div`
  position: relative;
  z-index: 2;
  margin-top: clamp(48px, 7vh, 88px);
  max-width: 760px;
`;

const Eyebrow = styled.p`
  margin: 0 0 18px;
  color: ${({ theme }) => theme.secondary[600]};
  font-size: 15px;
  font-weight: 750;
  letter-spacing: -0.01em;
`;

const HeroTitle = styled.h1`
  margin: 0;
  color: ${({ theme }) => theme.darkNavy};
  font-family: Pretendard, sans-serif;
  font-size: clamp(48px, 4.5vw, 72px);
  font-weight: 800;
  line-height: 1.08;
  letter-spacing: -0.055em;
`;

const Accent = styled.span`
  position: relative;
  display: inline-block;
  z-index: 0;

  &::after {
    content: '';
    position: absolute;
    z-index: -1;
    left: -0.03em;
    right: -0.04em;
    bottom: 0.08em;
    height: 0.28em;
    border-radius: 999px;
    background: color-mix(in oklch, ${({ theme }) => theme.primary} 46%, transparent);
  }
`;

const HeroDescription = styled.p`
  margin: 26px 0 0;
  max-width: 620px;
  color: ${({ theme }) => theme.darkNavyAlpha[800]};
  font-size: clamp(16px, 1.25vw, 19px);
  font-weight: 500;
  line-height: 1.68;
  letter-spacing: -0.02em;

  span {
    display: block;
  }
`;

const ProductVisual = styled.div`
  position: relative;
  z-index: 2;
  width: min(100%, 760px);
  margin-top: clamp(32px, 5vh, 56px);
  padding: 20px 20px 18px;
  border-radius: 28px;
  background: ${({ theme }) => theme.darkNavy};
  color: ${({ theme }) => theme.white};
  box-shadow: 0 22px 50px rgba(15, 42, 74, 0.18);
`;

const ProductTopBar = styled.div`
  min-height: 36px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

const ProductLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.whiteAlpha[900]};
  font-size: 12px;
  font-weight: 700;
`;

const LiveDot = styled.i`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ theme }) => theme.primary};
  box-shadow: 0 0 0 5px color-mix(in oklch, ${({ theme }) => theme.primary} 18%, transparent);
`;

const ProductLocation = styled.span`
  color: rgba(255, 255, 255, 0.54);
  font-size: 11px;
  font-weight: 500;
`;

const ProductBody = styled.div`
  min-height: 248px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 168px;
  gap: 18px;
`;

const SeatSection = styled.div`
  min-width: 0;
  padding: 22px;
  border: 1px solid rgba(255, 255, 255, 0.11);
  border-radius: 20px;
  background: color-mix(in oklch, ${({ theme }) => theme.darkNavy} 88%, ${({ theme }) => theme.primary});
`;

const SeatSectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
`;

const VisualCaption = styled.p`
  margin: 0 0 6px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 11px;
  font-weight: 600;
`;

const VisualTitle = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.whiteAlpha[900]};
  font-size: 15px;
  font-weight: 700;
  letter-spacing: -0.02em;
`;

const Availability = styled.span`
  flex: 0 0 auto;
  padding: 5px 9px;
  border-radius: 999px;
  background: color-mix(in oklch, ${({ theme }) => theme.primary} 19%, transparent);
  color: color-mix(in oklch, ${({ theme }) => theme.primary} 74%, white);
  font-size: 10px;
  font-weight: 700;
`;

const SeatGrid = styled.div`
  margin-top: 20px;
  display: grid;
  grid-template-columns: repeat(4, minmax(42px, 1fr));
  gap: 9px;
`;

const Seat = styled.span`
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid
    ${({ theme, $state }) =>
      $state === 'selected'
        ? theme.primary
        : $state === 'occupied'
          ? 'rgba(255, 255, 255, 0.08)'
          : 'rgba(255, 255, 255, 0.18)'};
  border-radius: 12px 12px 8px 8px;
  background:
    ${({ theme, $state }) =>
      $state === 'selected'
        ? theme.primary
        : $state === 'occupied'
          ? 'rgba(255, 255, 255, 0.06)'
          : 'rgba(255, 255, 255, 0.14)'};
  color: ${({ theme, $state }) => ($state === 'selected' ? theme.darkNavy : theme.whiteAlpha[900])};
  font-size: 11px;
  font-weight: 700;
`;

const SeatLegend = styled.div`
  margin-top: 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px 14px;
  color: rgba(255, 255, 255, 0.55);
  font-size: 9px;
  font-weight: 600;

  span {
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }

  i {
    width: 7px;
    height: 7px;
    border-radius: 3px;
    background: rgba(255, 255, 255, 0.16);
  }

  i[data-state='occupied'] {
    background: rgba(255, 255, 255, 0.07);
    border: 1px solid rgba(255, 255, 255, 0.12);
  }

  i[data-state='selected'] {
    background: ${({ theme }) => theme.primary};
  }
`;

const PhonePreview = styled.div`
  position: relative;
  min-width: 0;
  overflow: hidden;
  padding: 28px 15px 15px;
  border: 5px solid rgba(255, 255, 255, 0.17);
  border-radius: 25px;
  background: ${({ theme }) => theme.white};
  color: ${({ theme }) => theme.darkNavy};
`;

const PhoneNotch = styled.i`
  position: absolute;
  top: 8px;
  left: 50%;
  width: 42px;
  height: 5px;
  transform: translateX(-50%);
  border-radius: 999px;
  background: ${({ theme }) => theme.secondary[200]};
`;

const PhoneBrand = styled.p`
  margin: 0 0 20px;
  color: ${({ theme }) => theme.secondary[600]};
  font-size: 10px;
  font-weight: 800;
  letter-spacing: -0.02em;
`;

const PhoneGreeting = styled.p`
  margin: 0 0 3px;
  color: ${({ theme }) => theme.secondary[500]};
  font-size: 9px;
  font-weight: 600;
`;

const PhoneTitle = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.darkNavy};
  font-size: 13px;
  font-weight: 800;
  line-height: 1.35;
  letter-spacing: -0.03em;
`;

const PhoneAction = styled.div`
  margin-top: 19px;
  padding: 12px;
  border-radius: 12px;
  background: ${({ theme }) => theme.main[100]};

  span,
  strong {
    display: block;
  }

  span {
    color: ${({ theme }) => theme.secondary[500]};
    font-size: 8px;
    font-weight: 600;
  }

  strong {
    margin-top: 4px;
    color: ${({ theme }) => theme.darkNavy};
    font-size: 10px;
    font-weight: 800;
  }
`;

const PhoneButton = styled.span`
  min-height: 34px;
  margin-top: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
  background: ${({ theme }) => theme.primary};
  color: ${({ theme }) => theme.darkNavy};
  font-size: 9px;
  font-weight: 800;
`;

const ExperienceFlow = styled.div`
  position: relative;
  z-index: 2;
  margin-top: 22px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${({ theme }) => theme.darkNavyAlpha[800]};
  font-size: 12px;
  font-weight: 700;

  i {
    width: 18px;
    height: 1px;
    background: ${({ theme }) => theme.secondary[300]};
  }
`;

const DesktopAppPromo = styled.div`
  position: relative;
  z-index: 2;
  margin-top: auto;
  padding-top: 30px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
`;

const AppPromoCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;

  strong {
    color: ${({ theme }) => theme.darkNavy};
    font-size: 14px;
    font-weight: 750;
  }

  span {
    color: ${({ theme }) => theme.darkNavyAlpha[800]};
    font-size: 12px;
    font-weight: 500;
  }
`;

const HeroStoreButtons = styled(AppStoreButtons)`
  width: min(100%, 310px);
`;

const LoginPanel = styled.section`
  min-width: 0;
  padding: clamp(36px, 4vw, 64px) clamp(32px, 4vw, 56px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  border-left: 1px solid ${({ theme }) => theme.secondary[100]};
  background: ${({ theme }) => theme.white};
`;

const LoginContent = styled.div`
  width: 100%;
  max-width: 400px;
  margin: auto;
`;

const LoginHeader = styled.header`
  margin-bottom: 30px;
`;

const LoginKicker = styled.p`
  margin: 0 0 10px;
  color: ${({ theme }) => theme.secondary[600]};
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.12em;
`;

const LoginTitle = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.darkNavy};
  font-size: 32px;
  font-weight: 800;
  line-height: 1.25;
  letter-spacing: -0.04em;
`;

const LoginDescription = styled.p`
  margin: 10px 0 0;
  color: ${({ theme }) => theme.secondary[500]};
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
`;

const RoleTabs = styled.div`
  padding: 4px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 4px;
  border-radius: 12px;
  background: ${({ theme }) => theme.secondary[100]};
`;

const RoleTab = styled.button`
  min-height: 46px;
  border: 1px solid ${({ theme, $selected }) => ($selected ? theme.secondary[200] : 'transparent')};
  border-radius: 9px;
  background: ${({ theme, $selected }) => ($selected ? theme.white : 'transparent')};
  box-shadow: ${({ theme, $selected }) => ($selected ? theme.shadow.sm : 'none')};
  color: ${({ theme, $selected }) => ($selected ? theme.darkNavy : theme.secondary[500])};
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: color 160ms ease, background 160ms ease, box-shadow 160ms ease;

  &:focus-visible {
    outline: 3px solid color-mix(in oklch, ${({ theme }) => theme.primary} 28%, transparent);
    outline-offset: 2px;
  }
`;

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 26px;
`;

const Field = styled.div`
  position: relative;
`;

const FieldLabel = styled.label`
  margin: 0 0 8px;
  display: block;
  color: ${({ theme }) => theme.darkNavyAlpha[800]};
  font-size: 13px;
  font-weight: 700;
`;

const inputBase = css`
  width: 100%;
  height: 54px;
  border: 1px solid ${({ theme }) => theme.secondary[200]};
  border-radius: 12px;
  background: ${({ theme }) => theme.white};
  padding: 0 16px;
  font-size: 16px;
  font-weight: 500;
  color: ${({ theme }) => theme.darkNavy};
  transition: border-color 160ms ease, box-shadow 160ms ease;

  &::placeholder {
    color: ${({ theme }) => theme.secondary[400]};
    font-weight: 400;
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.primary};
    box-shadow: 0 0 0 3px color-mix(in oklch, ${({ theme }) => theme.primary} 16%, transparent);
  }
`;

const TextInput = styled.input`
  ${inputBase};
  padding-right: ${({ type, name }) => (name === 'password' ? '48px' : '16px')};
`;

const EyeButton = styled.button`
  position: absolute;
  bottom: 11px;
  right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: ${({ theme }) => theme.secondary[400]};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.secondary[100]};
    color: ${({ theme }) => theme.secondary[600]};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.primary};
  }
`;

const ForgotRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: -6px;
`;

const ForgotLink = styled.button`
  border: none;
  background: transparent;
  padding: 4px 0;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.secondary[500]};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.secondary[600]};
    text-decoration: underline;
    text-underline-offset: 3px;
  }
`;

const buttonBase = css`
  width: 100%;
  height: 54px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: filter 160ms ease, background 160ms ease, transform 80ms ease;

  &:active {
    transform: scale(0.99);
  }

  &:focus-visible {
    outline: 3px solid color-mix(in oklch, ${({ theme }) => theme.primary} 30%, transparent);
    outline-offset: 3px;
  }
`;

const PrimaryButton = styled.button`
  ${buttonBase};
  margin-top: 22px;
  border: none;
  color: ${({ theme }) => theme.darkNavy};
  background: ${({ theme }) => theme.primary};

  &:hover {
    filter: brightness(0.94);
  }
`;

const GhostButton = styled.button`
  ${buttonBase};
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1px solid ${({ theme }) => theme.secondary[200]};
  color: ${({ theme }) => theme.darkNavyAlpha[800]};
  background: ${({ theme }) => theme.white};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.secondary[100]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const JoinRow = styled.div`
  margin-top: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
`;

const JoinText = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${({ theme }) => theme.secondary[500]};
`;

const JoinButton = styled.button`
  min-height: 44px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.secondary[600]};
  font-size: 14px;
  font-weight: 750;
  text-decoration: underline;
  text-underline-offset: 4px;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.darkNavy};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.primary};
    outline-offset: 2px;
  }
`;

const MobileAppPromo = styled.div`
  margin-top: 32px;
  padding-top: 28px;
  display: none;
  flex-direction: column;
  gap: 16px;
  border-top: 1px solid ${({ theme }) => theme.secondary[100]};
`;

const LoginFooter = styled.p`
  width: 100%;
  max-width: 400px;
  margin: 34px auto 0;
  color: ${({ theme }) => theme.secondary[400]};
  font-size: 11px;
  font-weight: 500;
  line-height: 1.5;
  text-align: center;
`;

const ResponsiveStyles = createGlobalStyle`
@media (max-width: 1180px) {
  ${Shell} {
    grid-template-columns: minmax(0, 1fr) 420px;
  }

  ${BrandPanel} {
    padding: 40px;
  }

  ${HeroTitle} {
    font-size: clamp(46px, 5vw, 60px);
  }

  ${ProductBody} {
    grid-template-columns: minmax(0, 1fr) 150px;
  }

  ${DesktopAppPromo} {
    align-items: flex-start;
    flex-direction: column;
    gap: 14px;
  }
}

@media (max-width: 980px) {
  ${Shell} {
    display: block;
  }

  ${BrandPanel} {
    min-height: auto;
    padding: 28px 32px 36px;
  }

  ${HeroCopy} {
    margin-top: 38px;
    max-width: 700px;
  }

  ${HeroTitle} {
    font-size: clamp(42px, 7vw, 56px);
  }

  ${ProductVisual},
  ${ExperienceFlow},
  ${DesktopAppPromo} {
    display: none;
  }

  ${LoginPanel} {
    padding: 48px 24px 52px;
    border-top: 1px solid ${({ theme }) => theme.secondary[100]};
    border-left: none;
  }

  ${MobileAppPromo} {
    display: flex;
  }
}

@media (max-width: 640px) {
  ${BrandPanel} {
    padding: 22px 20px 26px;
  }

  ${Logo} {
    height: 30px;
  }

  ${SupportNote} {
    display: none;
  }

  ${HeroCopy} {
    margin-top: 28px;
  }

  ${Eyebrow} {
    margin-bottom: 10px;
    font-size: 13px;
  }

  ${HeroTitle} {
    font-size: clamp(34px, 10.2vw, 44px);
    line-height: 1.12;
  }

  ${HeroDescription} {
    margin-top: 15px;
    font-size: 14px;
    line-height: 1.55;

    span {
      display: inline;
    }

    span + span::before {
      content: ' ';
    }
  }

  ${LoginPanel} {
    padding: 34px 20px 44px;
  }

  ${LoginHeader} {
    margin-bottom: 24px;
  }

  ${LoginTitle} {
    font-size: 28px;
  }

  ${LoginFooter} {
    margin-top: 28px;
  }
}

@media (max-width: 380px) {
  ${BrandPanel} {
    padding-right: 16px;
    padding-left: 16px;
  }

  ${LoginPanel} {
    padding-right: 16px;
    padding-left: 16px;
  }

  ${HeroStoreButtons} {
    grid-template-columns: 1fr;
  }
}

@media (prefers-reduced-motion: reduce) {
  ${RoleTab},
  ${TextInput},
  ${PrimaryButton},
  ${GhostButton} {
    transition: none;
  }
`;
